/*
 * SeoBreakEvenCalculator
 * How many signed cases would cover the cost of an SEO contract, using only the reader's numbers.
 *
 * Rules:
 * - Inputs: monthly SEO fee F (>= 0), contract length M (whole months, >= 1),
 *   optional one-time setup fee S (>= 0, blank = 0), and the firm's own average fee revenue
 *   per signed case R (> 0).
 * - Total cost T = F * M + S.
 * - Cases to break even over the contract = ceil(T / R). If T is 0, the result is 0.
 * - Average per month = (T / R) / M, shown to one decimal place.
 * - Results appear once F, M and R are filled in and valid.
 * - A note always explains that this counts fee revenue only and that SEO results usually build
 *   over months, so early months tend to bring fewer cases than later ones.
 * No benchmarks, defaults, tracking, storage or network calls.
 */
import { useId, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Input } from "@/components/ui/input";

type Key = "fee" | "months" | "setup" | "perCase";

const FIELDS: { key: Key; label: string; help: string; placeholder: string; integer: boolean; optional?: boolean }[] = [
  { key: "fee", label: "Monthly SEO fee ($)", help: "From the proposal or your current contract", placeholder: "e.g. 4000", integer: false },
  { key: "months", label: "Contract length (months)", help: "The minimum term you'd commit to", placeholder: "e.g. 12", integer: true },
  { key: "setup", label: "One-time setup fee ($, optional)", help: "Leave blank if there isn't one", placeholder: "e.g. 2000", integer: false, optional: true },
  { key: "perCase", label: "Your average fee revenue per signed case ($)", help: "Your own figure, from your books", placeholder: "e.g. 9000", integer: false },
];

export default function SeoBreakEvenCalculator() {
  const baseId = useId();
  const [v, setV] = useState<Record<Key, string>>({ fee: "", months: "", setup: "", perCase: "" });

  const num = (s: string) => (s.trim() === "" ? null : Number(s));
  const F = num(v.fee);
  const M = num(v.months);
  const S = num(v.setup) ?? 0;
  const R = num(v.perCase);

  let error: string | null = null;
  const filled = F !== null && M !== null && R !== null;
  if (filled) {
    if (!Number.isFinite(F) || F < 0) error = "The monthly fee must be zero or more.";
    else if (!Number.isInteger(M) || M < 1) error = "Contract length must be a whole number of months, 1 or more.";
    else if (!Number.isFinite(S) || S < 0) error = "The setup fee must be zero or more, or left blank.";
    else if (!Number.isFinite(R) || R <= 0) error = "Average fee revenue per case must be more than zero.";
  }
  const ready = filled && !error && F !== null && M !== null && R !== null;

  const total = ready ? F * M + S : 0;
  const cases = ready ? (total === 0 ? 0 : Math.ceil(total / R)) : 0;
  const perMonth = ready ? total / R / M : 0;

  const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
  const focusRing =
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section className="not-prose my-8 rounded-xl border border-border bg-card p-5 text-card-foreground sm:p-6">
      <h3 className="text-lg font-semibold">How many cases would cover the cost of SEO?</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Use your own numbers. There are no defaults or industry averages here, and nothing is saved or sent.
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => {
          const id = `${baseId}-${f.key}`;
          const helpId = `${id}-help`;
          return (
            <div key={f.key}>
              <label htmlFor={id} className="block text-sm font-medium">
                {f.label}
              </label>
              <Input
                id={id}
                type="number"
                inputMode={f.integer ? "numeric" : "decimal"}
                min={f.key === "months" ? 1 : 0}
                step={f.integer ? 1 : "any"}
                value={v[f.key]}
                onChange={(e) => setV((p) => ({ ...p, [f.key]: e.target.value }))}
                placeholder={f.placeholder}
                aria-describedby={helpId}
                className={`mt-1 w-full rounded-md border border-input bg-card px-3 py-2 text-sm ${focusRing}`}
              />
              <p id={helpId} className="mt-1 text-xs text-muted-foreground">
                {f.help}
              </p>
            </div>
          );
        })}
      </div>

      <div aria-live="polite" className="mt-5 rounded-lg bg-muted p-4">
        {!filled && (
          <p className="text-sm text-muted-foreground">
            Fill in the monthly fee, contract length and your fee revenue per case to see the break-even point.
          </p>
        )}
        {error && <p className="text-sm">{error}</p>}
        {ready && (
          <div>
            <dl className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Total cost over the contract</dt>
                <dd className="text-lg font-semibold">{money(total)}</dd>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Signed cases to break even</dt>
                <dd className="text-lg font-semibold">{cases}</dd>
              </div>
              <div className="rounded-md border border-border bg-card p-3">
                <dt className="text-xs text-muted-foreground">Average per month</dt>
                <dd className="text-lg font-semibold">{perMonth.toFixed(1)}</dd>
              </div>
            </dl>
            <p className="mt-3 text-sm">
              Over {M} month{M === 1 ? "" : "s"}, about {cases} signed case{cases === 1 ? "" : "s"} from organic search
              would cover the cost. Anything beyond that is return on the investment.
            </p>
            <p className="mt-3 text-xs text-muted-foreground">
              This counts fee revenue only, not the cost of working each case. SEO results usually build over several
              months, so expect fewer cases early in a contract and more later on.{" "}
              <Link to="/pricing" className={`underline underline-offset-4 ${focusRing}`}>
                See how we price SEO
              </Link>
              .
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
